import { useState, useEffect } from "react";
import axiosInstance from "@/config/axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BarChart3, RefreshCw, AlertCircle, TrendingUp, Activity, HelpCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function OpenRouterStats() {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  
  // ElevenLabs stats
  const [loadingElevenLabs, setLoadingElevenLabs] = useState(false);
  const [elevenLabsStats, setElevenLabsStats] = useState(null);
  const [elevenLabsError, setElevenLabsError] = useState(null);

  useEffect(() => {
    fetchStats();
    fetchElevenLabsStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(`${API}/settings/openrouter-stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setStats(response.data);
      setLastUpdated(new Date());
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to fetch stats";
      setError(errorMsg);
      
      if (error.response?.status === 404) {
        console.log("OpenRouter API key not configured");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchElevenLabsStats = async () => {
    setLoadingElevenLabs(true);
    setElevenLabsError(null);
    
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(`${API}/settings/elevenlabs-stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setElevenLabsStats(response.data);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to fetch ElevenLabs stats";
      setElevenLabsError(errorMsg);
      
      if (error.response?.status === 404) {
        console.log("ElevenLabs API key not configured");
      }
    } finally {
      setLoadingElevenLabs(false);
    }
  };

  const handleRefresh = () => {
    fetchStats();
    fetchElevenLabsStats();
    toast.success("Stats refreshed successfully");
  };

  const formatNumber = (num) => {
    if (typeof num === 'string') return num;
    return num?.toLocaleString() || '0';
  };

  const getUsagePercentage = (used, limit) => {
    if (typeof limit === 'string') return 0;
    if (!limit || limit === 0) return 0;
    return Math.round((used / limit) * 100);
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return "text-red-600 bg-red-50";
    if (percentage >= 70) return "text-orange-600 bg-orange-50";
    if (percentage >= 50) return "text-yellow-600 bg-yellow-50";
    return "text-green-600 bg-green-50";
  };

  return (
    <div className="p-8" data-testid="openrouter-stats-page">
      <div className="max-w-7xl">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 mb-2">Usage Statistics</h1>
            <p className="text-slate-600">Real-time usage statistics for your AI services</p>
          </div>
          <Button
            onClick={handleRefresh}
            disabled={loading || loadingElevenLabs}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="refresh-stats-button"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${(loading || loadingElevenLabs) ? 'animate-spin' : ''}`} />
            {(loading || loadingElevenLabs) ? "Refreshing..." : "Refresh All Stats"}
          </Button>
        </div>
        
        {/* OpenRouter Section Header */}
        <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-2">
          <BarChart3 className="w-6 h-6 text-indigo-600" />
          OpenRouter Statistics
        </h2>

        {/* Error State */}
        {error && (
          <Card className="border-red-200 bg-red-50 mb-6">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-red-900 mb-1">Unable to fetch statistics</h3>
                  <p className="text-sm text-red-800 mb-3">{error}</p>
                  {error.includes("not configured") && (
                    <Link to="/settings/api-keys">
                      <Button variant="outline" size="sm" className="border-red-300 text-red-700 hover:bg-red-100">
                        Configure API Key
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Loading State */}
        {loading && !stats && (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <RefreshCw className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" />
              <p className="text-slate-600">Fetching your OpenRouter statistics...</p>
            </div>
          </div>
        )}

        {/* Stats Display */}
        {stats && !loading && (
          <>
            {/* Important Notice */}
            <div className="mb-6 bg-amber-50 border-l-4 border-amber-500 rounded-r-lg p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 text-2xl">⚠️</div>
                <div>
                  <h3 className="font-semibold text-amber-900 mb-1">Important: How Free Model Limits Work</h3>
                  <p className="text-sm text-amber-800 leading-relaxed">
                    All free model requests (DeepSeek, Qwen, Llama, etc.) count toward your <strong>account-wide daily limit</strong> of {stats.key_info.daily_limit} requests/day. 
                    You <strong>cannot</strong> use 200 requests per model. For example, if you use 100 DeepSeek + 100 Qwen requests, you've used 200 total from your account limit.
                  </p>
                </div>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <Card className="border-slate-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-600">Account Type</span>
                    <Activity className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div className="text-2xl font-bold text-slate-900">
                    {stats.key_info.is_free_tier ? (
                      <span className="text-green-600">Free Tier</span>
                    ) : (
                      <span className="text-indigo-600">Paid Tier</span>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="border-slate-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-600">Daily Limit</span>
                    <TrendingUp className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div className="text-2xl font-bold text-slate-900">
                    {formatNumber(stats.key_info.daily_limit)}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">requests per day</p>
                </CardContent>
              </Card>

              <Card className="border-slate-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-600">Usage Today</span>
                    <BarChart3 className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div className="text-2xl font-bold text-slate-900">
                    {formatNumber(stats.key_info.usage_today)}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {formatNumber(stats.key_info.remaining)} remaining
                  </p>
                </CardContent>
              </Card>
              
              <Card className="border-slate-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-600">This Week</span>
                    <BarChart3 className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="text-2xl font-bold text-slate-900">
                    {formatNumber(stats.key_info.usage_weekly || 0)}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {formatNumber(stats.key_info.usage_monthly || 0)} this month
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Models Table */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Model Usage Statistics
                </CardTitle>
                <CardDescription>
                  Detailed breakdown of requests per model
                  {lastUpdated && (
                    <span className="ml-2 text-xs">
                      (Last updated: {lastUpdated.toLocaleTimeString()})
                    </span>
                  )}
                  {stats.models.length === 1 && stats.models[0].model_id === "account-total" && (
                    <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block">
                      ⚠️ Per-model breakdown requires a provisioning API key. Showing account totals.
                    </div>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="stats-table">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 font-semibold text-slate-700">Model</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-700">Model ID</th>
                        <th className="text-right py-3 px-4 font-semibold text-slate-700">Requests<br/><span className="text-xs font-normal">(Today / Total)</span></th>
                        <th className="text-right py-3 px-4 font-semibold text-slate-700">Tokens<br/><span className="text-xs font-normal">(Prompt / Completion)</span></th>
                        <th className="text-right py-3 px-4 font-semibold text-slate-700">Total Tokens</th>
                        <th className="text-right py-3 px-4 font-semibold text-slate-700">Cost Today</th>
                        <th className="text-right py-3 px-4 font-semibold text-slate-700">Total Cost<br/><span className="text-xs font-normal">(30 days)</span></th>
                        <th className="text-right py-3 px-4 font-semibold text-slate-700">Limits<br/><span className="text-xs font-normal">(Daily / Rate)</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.models.map((model, index) => {
                        const percentage = getUsagePercentage(model.requests_used, model.requests_limit);
                        const colorClass = getUsageColor(percentage);
                        
                        return (
                          <tr
                            key={index}
                            className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                            data-testid="model-row"
                          >
                            <td className="py-4 px-4">
                              <div className="font-medium text-slate-900">{model.name}</div>
                            </td>
                            <td className="py-4 px-4">
                              <code className="text-xs bg-slate-100 px-2 py-1 rounded text-slate-700">
                                {model.model_id}
                              </code>
                            </td>
                            <td className="py-4 px-4 text-right">
                              <div className="font-semibold text-slate-900">{formatNumber(model.requests_used)}</div>
                              <div className="text-xs text-slate-500">{model.requests_total ? formatNumber(model.requests_total) : '-'} total</div>
                            </td>
                            <td className="py-4 px-4 text-right">
                              {model.prompt_tokens !== undefined ? (
                                <>
                                  <div className="text-sm text-slate-700">{formatNumber(model.prompt_tokens)}</div>
                                  <div className="text-xs text-slate-500">{formatNumber(model.completion_tokens)}</div>
                                </>
                              ) : '-'}
                            </td>
                            <td className="py-4 px-4 text-right font-semibold text-slate-900">
                              {model.total_tokens !== undefined ? formatNumber(model.total_tokens) : '-'}
                            </td>
                            <td className="py-4 px-4 text-right">
                              {model.cost_today !== undefined ? (
                                <span className="font-semibold text-slate-900">
                                  ${model.cost_today.toFixed(4)}
                                </span>
                              ) : '-'}
                            </td>
                            <td className="py-4 px-4 text-right">
                              {model.cost_total !== undefined ? (
                                <span className="font-bold text-green-700">
                                  ${model.cost_total.toFixed(4)}
                                </span>
                              ) : '-'}
                            </td>
                            <td className="py-4 px-4 text-right">
                              <div className="text-xs text-slate-600">{formatNumber(model.requests_limit)} / day</div>
                              <div className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded font-medium inline-block mt-1">
                                {model.rate_limit || "20/min"}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Info Box */}
                <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-lg">ℹ️</span>
                    </div>
                    <div className="text-sm text-blue-900">
                      <p className="font-semibold mb-1">About Rate Limits:</p>
                      <ul className="list-disc list-inside space-y-1 text-blue-800">
                        <li><strong>Account-wide limit (MOST RESTRICTIVE):</strong> 50 requests/day (new users) or 1000/day (with $10+ credits)</li>
                        <li><strong>Per free model:</strong> 200 requests/day max, 20 requests/minute - but counts toward account-wide limit</li>
                        <li><strong>Multiple free models:</strong> All free model requests count toward your account-wide limit (e.g., DeepSeek R1 + Qwen = max 1000/day total, not 200 each)</li>
                        <li><strong>Paid models:</strong> Account-wide limits apply, consume your OpenRouter credits</li>
                        <li>Limits reset daily at midnight UTC</li>
                        <li>Stats update in real-time from OpenRouter API</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* ElevenLabs Section */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-2">
            <svg className="w-6 h-6 text-indigo-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"></path>
              <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
              <line x1="12" x2="12" y1="19" y2="22"></line>
            </svg>
            ElevenLabs Statistics
          </h2>

          {/* ElevenLabs Error State */}
          {elevenLabsError && (
            <Card className="border-orange-200 bg-orange-50 mb-6">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-orange-900 mb-1">ElevenLabs stats unavailable</h3>
                    <p className="text-sm text-orange-800 mb-3">{elevenLabsError}</p>
                    {elevenLabsError.includes("not configured") && (
                      <Link to="/settings/communication">
                        <Button variant="outline" size="sm" className="border-orange-300 text-orange-700 hover:bg-orange-100">
                          Configure ElevenLabs API Key
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* ElevenLabs Loading */}
          {loadingElevenLabs && !elevenLabsStats && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <RefreshCw className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" />
                <p className="text-slate-600">Fetching ElevenLabs statistics...</p>
              </div>
            </div>
          )}

          {/* ElevenLabs Stats Display */}
          {elevenLabsStats && !loadingElevenLabs && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <Card className="border-slate-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-600">Subscription Tier</span>
                      <Activity className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div className="text-2xl font-bold text-slate-900 capitalize">
                      {elevenLabsStats.subscription_info.tier}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Status: {elevenLabsStats.subscription_info.status}
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-slate-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-600">Characters Used</span>
                      <BarChart3 className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div className="text-2xl font-bold text-slate-900">
                      {formatNumber(elevenLabsStats.subscription_info.character_count)}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      of {formatNumber(elevenLabsStats.subscription_info.character_limit)}
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-slate-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-600">Remaining</span>
                      <TrendingUp className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="text-2xl font-bold text-slate-900">
                      {formatNumber(elevenLabsStats.subscription_info.characters_remaining)}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">characters left</p>
                  </CardContent>
                </Card>

                <Card className="border-slate-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-600">Usage</span>
                      <BarChart3 className="w-5 h-5 text-purple-600" />
                    </div>
                    <div className="text-2xl font-bold text-slate-900">
                      {elevenLabsStats.subscription_info.usage_percentage.toFixed(1)}%
                    </div>
                    <p className="text-xs text-slate-500 mt-1">of quota used</p>
                  </CardContent>
                </Card>
              </div>

              {/* Usage Progress Bar */}
              <Card className="border-slate-200 shadow-sm mb-6">
                <CardHeader>
                  <CardTitle>Character Usage Overview</CardTitle>
                  <CardDescription>
                    Your current character usage for text-to-speech conversion
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-slate-700">
                        {formatNumber(elevenLabsStats.subscription_info.character_count)} / {formatNumber(elevenLabsStats.subscription_info.character_limit)} characters
                      </span>
                      <span className={`font-bold ${
                        elevenLabsStats.subscription_info.usage_percentage >= 90 ? 'text-red-600' :
                        elevenLabsStats.subscription_info.usage_percentage >= 70 ? 'text-orange-600' :
                        'text-green-600'
                      }`}>
                        {elevenLabsStats.subscription_info.usage_percentage.toFixed(1)}%
                      </span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-4">
                      <div
                        className={`h-4 rounded-full transition-all ${
                          elevenLabsStats.subscription_info.usage_percentage >= 90 ? 'bg-red-500' :
                          elevenLabsStats.subscription_info.usage_percentage >= 70 ? 'bg-orange-500' :
                          'bg-green-500'
                        }`}
                        style={{ width: `${Math.min(elevenLabsStats.subscription_info.usage_percentage, 100)}%` }}
                      ></div>
                    </div>
                    {elevenLabsStats.subscription_info.next_character_count_reset_unix > 0 && (
                      <p className="text-xs text-slate-500 mt-2">
                        Resets: {new Date(elevenLabsStats.subscription_info.next_character_count_reset_unix * 1000).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Additional Info */}
              <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle>Subscription Details</CardTitle>
                  <CardDescription>
                    Additional information about your ElevenLabs plan limits and capabilities
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <TooltipProvider>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center py-3 border-b border-slate-100">
                        <div className="flex items-center gap-2">
                          <span className="text-slate-700 font-medium">Custom Voice Limit</span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <HelpCircle className="w-4 h-4 text-slate-400 cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs bg-slate-900 text-white">
                              <p>The maximum number of custom voice clones you can create and store in your account. This includes instant voice clones and professional voice clones.</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <span className="font-semibold text-slate-900">
                          {elevenLabsStats.voice_limit || 'Unlimited'}
                        </span>
                      </div>
                      
                      <div className="flex justify-between items-center py-3 border-b border-slate-100">
                        <div className="flex items-center gap-2">
                          <span className="text-slate-700 font-medium">Can Purchase Extra Characters</span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <HelpCircle className="w-4 h-4 text-slate-400 cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs bg-slate-900 text-white">
                              <p>Indicates whether your current subscription tier allows you to purchase additional character quota beyond your monthly limit. Upgrade your plan if you need more characters regularly.</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <span className={`font-semibold ${elevenLabsStats.can_extend_character_limit ? 'text-green-600' : 'text-slate-500'}`}>
                          {elevenLabsStats.can_extend_character_limit ? 'Yes' : 'No'}
                        </span>
                      </div>
                    </div>
                  </TooltipProvider>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
